/*
  How It Works and About Us pages (templates/page.how-it-works.liquid,
  templates/page.about-us.liquid): the tier tabs, the two swipe rows, and the video
  pop-up. Every interaction is announced as a bubbling `wls:*` CustomEvent on the
  element it happened to; assets/analytics-posthog-funnel.js (initStoryPages) turns
  them into PostHog events. Loaded async, so it never waits behind the theme's
  deferred scripts.
*/
(function () {
  'use strict';
  if (window.__wlsPages) return;
  window.__wlsPages = true;

  function announce(el, name, detail) {
    try {
      el.dispatchEvent(new CustomEvent('wls:' + name, { bubbles: true, detail: detail || {} }));
    } catch (e) {}
  }

  /* ---------- Tier tabs (sections/hiw-tiers.liquid) ---------- */
  function initTabs(root) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    }
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        if (tab.getAttribute('aria-selected') === 'true') return;
        select(tab, false);
        announce(tab, 'tier_selected', { tier: tab.getAttribute('data-wls-tier') });
      });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        e.preventDefault();
        select(next, true);
        announce(next, 'tier_selected', { tier: next.getAttribute('data-wls-tier') });
      });
    });
  }

  /* ---------- Swipe rows: the review carousel's controls (tp-reviews-carousel__*, assets/tp-reviews.css) ---------- */
  function initCarousel(root) {
    var track = root.querySelector('.wls-track');
    var prev = root.querySelector('[data-wls-prev]');
    var next = root.querySelector('[data-wls-next]');
    var fill = root.querySelector('.tp-reviews-carousel__progress-fill');
    var controls = root.querySelector('[data-wls-controls]');
    if (!track) return;
    var name = root.getAttribute('data-wls-carousel');

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var overflow = max > 2;
      if (controls) controls.hidden = !overflow;
      if (!overflow) return;
      // The review carousel's fill grows with the position (assets/tp-reviews.js): the visible
      // share at the start, 100% at the end.
      var share = track.clientWidth / track.scrollWidth;
      var pos = Math.min(1, Math.max(0, track.scrollLeft / max));
      if (fill) fill.style.width = (share + pos * (1 - share)) * 100 + '%';
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max - 2;
    }
    function step(dir) {
      var item = track.firstElementChild;
      if (!item) return;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      var by = item.getBoundingClientRect().width + gap;
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: dir * by, behavior: reduce ? 'auto' : 'smooth' });
      announce(root, 'carousel_moved', { carousel: name, direction: dir > 0 ? 'next' : 'prev' });
    }
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    track.addEventListener('scroll', update, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    else window.addEventListener('resize', update);
    update();
  }

  /* ---------- Video pop-up (snippets/wls-video-dialog.liquid) ---------- */
  var dialog = null;
  var opener = null;
  var openedAt = 0;
  var current = null;

  function closeDialog() {
    if (dialog && dialog.open) dialog.close();
  }

  function openVideo(btn) {
    dialog = dialog || document.getElementById('wls-video-dialog');
    if (!dialog || typeof dialog.showModal !== 'function') {
      var href = btn.getAttribute('data-wls-href');
      if (href) window.open(href, '_blank', 'noopener');
      return;
    }
    var id = btn.getAttribute('data-wls-youtube') || '';
    var title = btn.getAttribute('data-wls-title') || '';
    var frame = dialog.querySelector('.wls-dialog__frame');
    var placeholder = dialog.querySelector('.wls-dialog__placeholder');
    frame.querySelectorAll('iframe').forEach(function (f) { f.remove(); });
    dialog.classList.toggle('wls-dialog--portrait', btn.hasAttribute('data-wls-portrait') && window.matchMedia('(max-width: 749px)').matches);
    if (id) {
      placeholder.hidden = true;
      var iframe = document.createElement('iframe');
      iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0&playsinline=1';
      iframe.title = title || 'Video';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      frame.appendChild(iframe);
    } else {
      placeholder.hidden = false;
    }
    dialog.setAttribute('aria-label', title || 'Video');
    opener = btn;
    openedAt = Date.now();
    current = { video: btn.getAttribute('data-wls-video') || id || 'placeholder', placement: btn.getAttribute('data-wls-placement') || null, has_video: !!id };
    dialog.showModal();
    var close = dialog.querySelector('.wls-dialog__close');
    if (close) close.focus();
    announce(btn, 'video_opened', current);
  }

  function initDialog() {
    dialog = document.getElementById('wls-video-dialog');
    if (!dialog) return;
    dialog.addEventListener('click', function (e) {
      // A click on the backdrop lands on the dialog element itself.
      if (e.target === dialog || (e.target.closest && e.target.closest('.wls-dialog__close'))) closeDialog();
    });
    dialog.addEventListener('close', function () {
      dialog.querySelectorAll('iframe').forEach(function (f) { f.remove(); });
      if (current) {
        var detail = { video: current.video, placement: current.placement, has_video: current.has_video, open_ms: Date.now() - openedAt };
        announce(opener || document.body, 'video_closed', detail);
      }
      if (opener && opener.focus) opener.focus();
      current = null;
    });
  }

  function init() {
    initDialog();
    document.querySelectorAll('[data-wls-tabs]').forEach(initTabs);
    document.querySelectorAll('[data-wls-carousel]').forEach(initCarousel);
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-wls-video]') : null;
      if (!btn) return;
      e.preventDefault();
      openVideo(btn);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
